/**
 * i18n light senza dipendenze: rileva la lingua del browser e ritorna le
 * stringhe nella lingua corretta. Italiano e' il default per qualunque
 * locale non esplicitamente supportata.
 *
 * Lingue supportate al momento: it (default), en.
 * Per aggiungere una lingua: estendere SupportedLocale + STRINGS.
 *
 * La lingua viene rilevata UNA volta al primo import del modulo. Se l'utente
 * cambia la lingua del browser, deve ricaricare la pagina.
 */

export type SupportedLocale = "it" | "en";

const DEFAULT_LOCALE: SupportedLocale = "it";

/**
 * Estrae il primary tag (es. "it" da "it-IT") dalla `navigator.language` /
 * `navigator.languages`. Fallback DEFAULT_LOCALE se window e' indisponibile
 * (SSR) o se la lingua del browser non e' nelle SupportedLocale.
 */
export function detectLocale(): SupportedLocale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  // navigator.languages e' la lista di preferenze ordinata; ripiega su language.
  const candidates: string[] = (navigator.languages && navigator.languages.length > 0
    ? Array.from(navigator.languages)
    : [navigator.language]
  ).filter(Boolean);
  for (const c of candidates) {
    const primary = c.toLowerCase().split("-")[0];
    if (primary === "it" || primary === "en") return primary;
  }
  return DEFAULT_LOCALE;
}

export const LOCALE: SupportedLocale = detectLocale();

/**
 * Nome lingua in inglese (per system prompt Gemini: "answer in Italian").
 * Estendere insieme a SupportedLocale.
 */
export const LANGUAGE_NAME_EN: Record<SupportedLocale, string> = {
  it: "Italian",
  en: "English",
};

type StringKey =
  | "ai.title"
  | "ai.subtitle"
  | "ai.placeholder"
  | "ai.askButton"
  | "ai.askingLabel"
  | "ai.thinking"
  | "ai.error"
  | "ai.suggestion.signature"
  | "ai.suggestion.ambiance"
  | "ai.suggestion.occasion";

const STRINGS: Record<SupportedLocale, Record<StringKey, string>> = {
  it: {
    "ai.title": "Assistente AI",
    "ai.subtitle": "Chiedi qualcosa su {name}",
    "ai.placeholder": "es.: qual e' un piatto da non perdere?",
    "ai.askButton": "Chiedi",
    "ai.askingLabel": "...",
    "ai.thinking": "Sto pensando...",
    "ai.error": "Impossibile ottenere una risposta dall'assistente AI.",
    "ai.suggestion.signature": "Qual e' il piatto signature?",
    "ai.suggestion.ambiance": "Com'e' l'atmosfera?",
    "ai.suggestion.occasion": "E' adatto per un'occasione speciale?",
  },
  en: {
    "ai.title": "AI Assistant",
    "ai.subtitle": "Ask anything about {name}",
    "ai.placeholder": "e.g., what's a must-try dish?",
    "ai.askButton": "Ask",
    "ai.askingLabel": "...",
    "ai.thinking": "Thinking...",
    "ai.error": "Failed to get a response from the AI assistant.",
    "ai.suggestion.signature": "What's the signature dish?",
    "ai.suggestion.ambiance": "What's the ambiance like?",
    "ai.suggestion.occasion": "Is it good for a special occasion?",
  },
};

/**
 * Recupera una stringa localizzata. Supporta interpolazione semplice di
 * `{var}` con il valore in `vars[var]`. Fallback all'italiano (e poi alla
 * chiave stessa) se la traduzione manca.
 */
export function t(key: StringKey, vars?: Record<string, string>): string {
  const raw = STRINGS[LOCALE]?.[key] ?? STRINGS[DEFAULT_LOCALE]?.[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}
